import type { Line } from "./types"

/**
 * 完了記録の子行 (`completed_tasks.notes_data`) の入出力を 1 箇所に集約する。
 *
 * ## なぜこのモジュールがあるか
 * 完了時、タスクの子行 (サブタスク = `type: "task"` / コメント = `type: "task-note"`) は
 * `lines` から外れて完了記録の `notes` へ移る。2026-09-04 まで `notes_data` は
 * `id` / `content` / `parentTaskId` しか保存しておらず、読み戻すときに全件を
 * `task-note` として組み立てていたため、**「未完了に戻す」でサブタスクが
 * コメントに化けていた**（復元のたびに構造が失われる不可逆な劣化）。
 *
 * ## 後方互換
 * この修正より前の記録には `type` が無い。`type` が無い / 未知の値のときは
 * **従来どおり `task-note` にフォールバックする**（過去の記録の見え方を変えない）。
 * 同じく `createdAt` が無ければ完了時刻を使う（従来の挙動）。
 *
 * ## mobile との関係
 * mobile (`mobile/src/lib/completed-task-notes.ts`) が同じ形式を読み書きする。
 * **片方だけ形式を変えると、もう片方が保存した記録を読んだときに化ける**ので、
 * フィールドを増減するときは必ず両方を同時に変えること。
 * 一致は `__tests__/lib/completed-task-notes.test.ts` が検査している。
 *
 * ## 保存するフィールドを絞っている理由
 * 完了記録は「表示 + 未完了に戻す」ためのスナップショットなので、
 * 戻したときに見た目・構造が変わるものだけを持つ。
 * - `type`   : サブタスク / コメントの区別（このバグの本体）
 * - `status` : サブタスクのチェック状態
 * - `indentLevel` / `bulletPoint` : 行の見え方
 * - `assignee` : Claude が残したコメントの署名
 * - `createdAt` : コメントの表示時刻（無いと全件が完了時刻になる）
 * `scheduledDate` や `tags` のように子行では使われないフィールドは意図的に持たない。
 */
export interface CompletedNoteData {
  id: string
  content: string
  parentTaskId?: string
  type?: Line["type"]
  status?: Line["status"]
  indentLevel?: number
  bulletPoint?: Line["bulletPoint"]
  assignee?: Line["assignee"]
  createdAt?: number
}

const KNOWN_NOTE_TYPES: ReadonlyArray<Line["type"]> = ["text", "task", "task-note"]

/** 完了記録に無い / 壊れている `type` の受け皿（従来の挙動 = コメント扱い） */
export const FALLBACK_NOTE_TYPE: Line["type"] = "task-note"

/** 保存されている値から行の type を決める。未知の値は必ずフォールバックする */
export function completedNoteType(raw: unknown): Line["type"] {
  const type = (raw as { type?: unknown } | null | undefined)?.type
  return KNOWN_NOTE_TYPES.includes(type as Line["type"])
    ? (type as Line["type"])
    : FALLBACK_NOTE_TYPE
}

/** Line → notes_data の 1 要素 */
export function serializeCompletedNote(note: Line): CompletedNoteData {
  return {
    id: note.id,
    content: note.content,
    parentTaskId: note.parentTaskId,
    type: note.type,
    status: note.status,
    indentLevel: note.indentLevel,
    bulletPoint: note.bulletPoint,
    assignee: note.assignee,
    createdAt: note.createdAt,
  }
}

/** notes_data の 1 要素 → Line。`completedAt` は createdAt が無い旧記録のための既定値 */
export function deserializeCompletedNote(raw: any, completedAt: number): Line {
  return {
    id: raw?.id,
    type: completedNoteType(raw),
    content: raw?.content || "",
    status: raw?.status,
    parentTaskId: raw?.parentTaskId,
    indentLevel: raw?.indentLevel,
    bulletPoint: raw?.bulletPoint,
    assignee: raw?.assignee,
    createdAt: typeof raw?.createdAt === "number" ? raw.createdAt : completedAt,
  }
}
